import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "../../api/client";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../ui/dialog";
import { BookmarkSimple, Trash, FloppyDisk, X } from "@phosphor-icons/react";
import { BRAND } from "../Brand";

// Views bar — lists shared per-project saved views. A "view" persists:
//   zoom, showCritical, showSlack, filterAssignee, hideCompleted, groupBy
// The parent Plan component supplies currentConfig and applyConfig callback.

export default function ViewsBar({ projectId, canEdit, currentConfig, onApply }) {
  const [views, setViews] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [saveOpen, setSaveOpen] = useState(false);
  const [saveName, setSaveName] = useState("");

  const load = async () => {
    if (!projectId) { setViews([]); return; }
    try {
      const { data } = await api.get("/plan/views", { params: { project_id: projectId } });
      setViews(data.views || []);
      // apply default if none active yet
      if (!activeId) {
        const def = (data.views || []).find((v) => v.is_default);
        if (def && onApply) {
          onApply(def.config || {});
          setActiveId(def.id);
        }
      }
    } catch { setViews([]); }
  };
  useEffect(() => { load(); }, [projectId]); // eslint-disable-line react-hooks/exhaustive-deps

  const applyView = (v) => {
    setActiveId(v.id);
    onApply && onApply(v.config || {});
    toast.success(`Applied "${v.name}"`);
  };

  const doSave = async () => {
    const name = saveName.trim();
    if (!name) { toast.error("View name required"); return; }
    try {
      const { data } = await api.post("/plan/views", {
        project_id: projectId,
        name,
        config: currentConfig || {},
        is_default: false,
      });
      toast.success(`View "${name}" saved`);
      setSaveName("");
      setSaveOpen(false);
      setActiveId(data.id);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    }
  };

  const doUpdate = async (view) => {
    try {
      await api.patch(`/plan/views/${view.id}`, {
        project_id: projectId,
        name: view.name,
        config: currentConfig || {},
      });
      toast.success(`View "${view.name}" updated`);
      load();
    } catch { toast.error("Update failed"); }
  };

  const doDelete = async (view) => {
    if (!window.confirm(`Delete view "${view.name}"?`)) return;
    try {
      await api.delete(`/plan/views/${view.id}`);
      toast.success("View deleted");
      if (activeId === view.id) setActiveId(null);
      load();
    } catch { toast.error("Delete failed"); }
  };

  const setDefault = async (view) => {
    try {
      await api.patch(`/plan/views/${view.id}`, {
        project_id: projectId,
        name: view.name,
        config: view.config || {},
        is_default: true,
      });
      toast.success(`"${view.name}" set as default`);
      load();
    } catch { toast.error("Update failed"); }
  };

  if (!projectId) return null;

  return (
    <div className="flex items-center gap-1.5 flex-wrap" data-testid="plan-views-bar">
      <div className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-semibold text-neutral-500">
        <BookmarkSimple size={12} weight="bold" /> Views
      </div>
      {views.length === 0 ? (
        <span className="text-xs text-neutral-400">None saved</span>
      ) : views.map((v) => (
        <div key={v.id}
             className={`inline-flex items-center rounded-md border overflow-hidden ${
               activeId === v.id ? "border-neutral-900" : "border-neutral-200"
             }`}
             data-testid={`plan-view-${v.id}`}>
          <button
            type="button"
            onClick={() => applyView(v)}
            className={`h-8 px-2.5 text-xs font-semibold ${
              activeId === v.id ? "bg-neutral-900 text-white" : "text-neutral-700 hover:bg-neutral-50"
            }`}
            data-testid={`plan-view-apply-${v.id}`}
          >
            {v.name}{v.is_default ? " ★" : ""}
          </button>
          {canEdit ? (
            <>
              <div className="w-px h-5 bg-neutral-200" />
              <button
                type="button"
                onClick={() => doUpdate(v)}
                title="Overwrite with current settings"
                className="h-8 px-1.5 text-neutral-500 hover:text-neutral-900 hover:bg-neutral-50"
                data-testid={`plan-view-update-${v.id}`}
              >
                <FloppyDisk size={11} weight="bold" />
              </button>
              <button
                type="button"
                onClick={() => setDefault(v)}
                title="Set as default view"
                className="h-8 px-1.5 text-neutral-400 hover:text-orange-500 hover:bg-neutral-50"
                data-testid={`plan-view-default-${v.id}`}
              >
                ★
              </button>
              <button
                type="button"
                onClick={() => doDelete(v)}
                className="h-8 px-1.5 text-neutral-500 hover:text-red-500 hover:bg-neutral-50"
                data-testid={`plan-view-delete-${v.id}`}
              >
                <X size={11} weight="bold" />
              </button>
            </>
          ) : null}
        </div>
      ))}
      {canEdit ? (
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={() => setSaveOpen(true)}
          className="h-8"
          data-testid="plan-view-save-btn"
        >
          <FloppyDisk size={11} weight="bold" className="mr-1" /> Save view
        </Button>
      ) : null}

      <Dialog open={saveOpen} onOpenChange={setSaveOpen}>
        <DialogContent className="max-w-sm" data-testid="plan-view-save-dialog">
          <DialogHeader>
            <DialogTitle>Save view</DialogTitle>
          </DialogHeader>
          <div>
            <label className="text-[10px] uppercase tracking-widest font-semibold text-neutral-600">Name</label>
            <Input
              value={saveName}
              onChange={(e) => setSaveName(e.target.value)}
              placeholder="e.g. Team weekly"
              className="h-9 mt-1"
              data-testid="plan-view-save-name"
              autoFocus
            />
            <div className="text-[11px] text-neutral-500 mt-2">
              Saves current zoom, critical toggle, slack toggle, assignee filter, and hide-completed setting. Shared with everyone on this project.
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" type="button" onClick={() => setSaveOpen(false)}>Cancel</Button>
            <Button type="button" onClick={doSave} className="text-white" style={{ backgroundColor: BRAND.colors.blue }} data-testid="plan-view-save-confirm">
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
